/**
 * Keyboard Shortcuts System
 * Provides global keyboard shortcuts for navigation and actions
 */

export type ShortcutAction = 
  | 'navigate_dashboard'
  | 'navigate_search'
  | 'navigate_analytics'
  | 'navigate_customers'
  | 'navigate_projects'
  | 'navigate_invoices'
  | 'navigate_monitor'
  | 'open_search'
  | 'focus_search'
  | 'toggle_sidebar'
  | 'logout'
  | 'open_help'
  | 'refresh_page'
  | 'toggle_theme';

export interface Shortcut {
  action: ShortcutAction;
  keys: string[];
  description: string;
  category: 'navigation' | 'search' | 'actions' | 'general';
  isGlobal: boolean;
  requiresAuth: boolean;
}

export const KEYBOARD_SHORTCUTS: Record<ShortcutAction, Shortcut> = {
  // Navigation
  navigate_dashboard: {
    action: 'navigate_dashboard',
    keys: ['g', 'd'],
    description: 'Go to Dashboard',
    category: 'navigation',
    isGlobal: true,
    requiresAuth: true,
  },
  navigate_search: {
    action: 'navigate_search',
    keys: ['g', 's'],
    description: 'Go to Global Search',
    category: 'navigation',
    isGlobal: true,
    requiresAuth: true,
  },
  navigate_analytics: {
    action: 'navigate_analytics',
    keys: ['g', 'a'],
    description: 'Go to Analytics',
    category: 'navigation',
    isGlobal: true,
    requiresAuth: true,
  },
  navigate_customers: {
    action: 'navigate_customers',
    keys: ['g', 'c'],
    description: 'Go to Customers',
    category: 'navigation',
    isGlobal: true,
    requiresAuth: true,
  },
  navigate_projects: {
    action: 'navigate_projects',
    keys: ['g', 'p'],
    description: 'Go to Projects',
    category: 'navigation',
    isGlobal: true,
    requiresAuth: true,
  },
  navigate_invoices: {
    action: 'navigate_invoices',
    keys: ['g', 'i'],
    description: 'Go to Invoices',
    category: 'navigation',
    isGlobal: true,
    requiresAuth: true,
  },
  navigate_monitor: {
    action: 'navigate_monitor',
    keys: ['g', 'm'],
    description: 'Go to System Monitor',
    category: 'navigation',
    isGlobal: true,
    requiresAuth: true,
  },

  // Search
  open_search: {
    action: 'open_search',
    keys: ['Control', 'k'],
    description: 'Open Search Dialog',
    category: 'search',
    isGlobal: true,
    requiresAuth: true,
  },
  focus_search: {
    action: 'focus_search',
    keys: ['/'],
    description: 'Focus Search Input',
    category: 'search',
    isGlobal: true,
    requiresAuth: true,
  },

  // Actions
  toggle_sidebar: {
    action: 'toggle_sidebar',
    keys: ['Control', 'b'],
    description: 'Toggle Sidebar',
    category: 'actions',
    isGlobal: true,
    requiresAuth: true,
  },
  refresh_page: {
    action: 'refresh_page',
    keys: ['Control', 'r'],
    description: 'Refresh Page',
    category: 'actions',
    isGlobal: true,
    requiresAuth: false,
  },
  logout: {
    action: 'logout',
    keys: ['Control', 'Shift', 'l'],
    description: 'Logout',
    category: 'actions',
    isGlobal: true,
    requiresAuth: true,
  },

  // General
  open_help: {
    action: 'open_help',
    keys: ['?'],
    description: 'Open Keyboard Shortcuts Help',
    category: 'general',
    isGlobal: true,
    requiresAuth: false,
  },
  toggle_theme: {
    action: 'toggle_theme',
    keys: ['Control', 'Alt', 't'],
    description: 'Toggle Theme (Light/Dark)',
    category: 'general',
    isGlobal: true,
    requiresAuth: false,
  },
};

/**
 * Format shortcut keys for display
 */
export const formatShortcutKeys = (keys: string[]): string => {
  return keys
    .map((key) => {
      if (key === 'Control') return 'Ctrl';
      if (key === 'Shift') return 'Shift';
      if (key === 'Alt') return 'Alt';
      return key.length === 1 ? key.toUpperCase() : key;
    })
    .join(' + ');
};

/**
 * Get shortcuts by category
 */
export const getShortcutsByCategory = (
  category: 'navigation' | 'search' | 'actions' | 'general'
): Shortcut[] => {
  return Object.values(KEYBOARD_SHORTCUTS).filter(
    (shortcut) => shortcut.category === category
  );
};

/**
 * Check if key combination matches shortcut
 */
export const matchesShortcut = (
  event: KeyboardEvent,
  shortcutKeys: string[]
): boolean => {
  const pressedKeys: string[] = [];

  if (event.ctrlKey || event.metaKey) pressedKeys.push('Control');
  if (event.shiftKey) pressedKeys.push('Shift');
  if (event.altKey) pressedKeys.push('Alt');

  const keyValue = event.key.toLowerCase();
  if (!['control', 'shift', 'alt'].includes(keyValue)) {
    pressedKeys.push(keyValue === ' ' ? ' ' : keyValue);
  }

  return (
    pressedKeys.length === shortcutKeys.length &&
    shortcutKeys.every((key) =>
      pressedKeys.some((k) => k.toLowerCase() === key.toLowerCase())
    )
  );
};

/**
 * Get all global shortcuts
 */
export const getGlobalShortcuts = (): Shortcut[] => {
  return Object.values(KEYBOARD_SHORTCUTS).filter((s) => s.isGlobal);
};

/**
 * Register keyboard shortcut listener
 */
export const registerShortcutListener = (
  callback: (action: ShortcutAction) => void,
  isAuthenticated: boolean = true
): (() => void) => {
  const handleKeyDown = (event: KeyboardEvent) => {
    // Skip if typing in input/textarea
    const target = event.target as HTMLElement;
    if (
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.contentEditable === 'true'
    ) {
      // Allow Ctrl+K in search fields
      if (
        event.ctrlKey &&
        (event.key === 'k' || event.key === 'K') &&
        target.tagName === 'INPUT'
      ) {
        event.preventDefault();
        callback('open_search');
      }
      return;
    }

    // Check all shortcuts
    for (const shortcut of Object.values(KEYBOARD_SHORTCUTS)) {
      if (shortcut.requiresAuth && !isAuthenticated) {
        continue;
      }

      if (matchesShortcut(event, shortcut.keys)) {
        event.preventDefault();
        callback(shortcut.action);
        break;
      }
    }
  };

  window.addEventListener('keydown', handleKeyDown);

  // Return cleanup function
  return () => {
    window.removeEventListener('keydown', handleKeyDown);
  };
};

/**
 * Create shortcut help text
 */
export const getShortcutHelpText = (action: ShortcutAction): string => {
  const shortcut = KEYBOARD_SHORTCUTS[action];
  if (!shortcut) return '';
  return `${shortcut.description} (${formatShortcutKeys(shortcut.keys)})`;
};

/**
 * Check if a key combination is valid
 */
export const isValidShortcutCombo = (keys: string[]): boolean => {
  if (keys.length === 0) return false;
  if (keys.length > 3) return false;
  
  const modifiers = ['Control', 'Shift', 'Alt'];
  const nonModifiers = keys.filter((k) => !modifiers.includes(k));
  
  return nonModifiers.length === 1;
};

/**
 * Get shortcut for action
 */
export const getShortcut = (action: ShortcutAction): Shortcut | null => {
  return KEYBOARD_SHORTCUTS[action] || null;
};

/**
 * Create keyboard shortcut reference card data
 */
export const getShortcutReferenceData = (): Record<
  'navigation' | 'search' | 'actions' | 'general',
  Array<{ keys: string; description: string }>
> => {
  const reference = {
    navigation: [] as Array<{ keys: string; description: string }>,
    search: [] as Array<{ keys: string; description: string }>,
    actions: [] as Array<{ keys: string; description: string }>,
    general: [] as Array<{ keys: string; description: string }>,
  };

  Object.values(KEYBOARD_SHORTCUTS).forEach((shortcut) => {
    reference[shortcut.category].push({
      keys: formatShortcutKeys(shortcut.keys),
      description: shortcut.description,
    });
  });

  return reference;
};
